import {
  GROUPS,
  D1_OPTIONS,
  D2_OPTIONS,
  D3_OPTIONS,
  R2_OPTIONS,
  D1_QUESTION,
  D2_QUESTION,
  D3_QUESTION,
  R2_QUESTION,
  answeredCount,
  TOTAL_QUESTIONS,
  type SelfAssessmentAnswers,
} from '../lib/selfAssessment';

interface Props {
  answers: SelfAssessmentAnswers;
  onChange: (next: SelfAssessmentAnswers) => void;
}

export const SelfAssessmentForm = ({ answers, onChange }: Props) => {
  const setScale = (id: keyof SelfAssessmentAnswers, value: number) => {
    onChange({ ...answers, [id]: value });
  };
  const setChoice = <K extends keyof SelfAssessmentAnswers>(id: K, value: SelfAssessmentAnswers[K]) => {
    onChange({ ...answers, [id]: value });
  };

  const groupA = GROUPS[0];
  const groupB = GROUPS[1];
  const groupC = GROUPS[2];
  const groupR = GROUPS[3];

  return (
    <div className="assessment-form">
      <div className="assessment-progress">
        <span>
          {answeredCount(answers)} of {TOTAL_QUESTIONS} answered
        </span>
        <div className="assessment-progress-bar">
          <div
            className="assessment-progress-fill"
            style={{ width: `${(answeredCount(answers) / TOTAL_QUESTIONS) * 100}%` }}
          />
        </div>
      </div>

      <ScaleGroup title={groupA.title} questions={groupA.scaleQuestions} answers={answers} onSet={setScale} />
      <ScaleGroup title={groupB.title} questions={groupB.scaleQuestions} answers={answers} onSet={setScale} />
      <ScaleGroup title={groupC.title} questions={groupC.scaleQuestions} answers={answers} onSet={setScale} />

      <div className="assessment-group">
        <h3 className="assessment-group-title">Incident History & Impact</h3>

        <ChoiceQuestion
          number="D.1"
          text={D1_QUESTION}
          options={D1_OPTIONS}
          value={answers.d1}
          onSelect={(v) => setChoice('d1', v)}
        />
        <ChoiceQuestion
          number="D.2"
          text={D2_QUESTION}
          options={D2_OPTIONS}
          value={answers.d2}
          onSelect={(v) => setChoice('d2', v)}
        />
        <ChoiceQuestion
          number="D.3"
          text={D3_QUESTION}
          options={D3_OPTIONS}
          value={answers.d3}
          onSelect={(v) => setChoice('d3', v)}
        />
      </div>

      <div className="assessment-group">
        <h3 className="assessment-group-title">{groupR.title}</h3>
        <p className="assessment-group-note">
          These don't affect your score — they just help us personalize your report.
        </p>
        <ScaleQuestionRow question={groupR.scaleQuestions[0]} value={answers.r1} onSelect={(v) => setScale('r1', v)} />
        <ChoiceQuestion
          number="R.2"
          text={R2_QUESTION}
          options={R2_OPTIONS}
          value={answers.r2}
          onSelect={(v) => setChoice('r2', v)}
        />
        <ScaleQuestionRow question={groupR.scaleQuestions[1]} value={answers.r3} onSelect={(v) => setScale('r3', v)} />
      </div>
    </div>
  );
};

const ScaleGroup = ({
  title,
  questions,
  answers,
  onSet,
}: {
  title: string;
  questions: { id: keyof SelfAssessmentAnswers; number: string; text: string; loLabel: string; hiLabel: string }[];
  answers: SelfAssessmentAnswers;
  onSet: (id: keyof SelfAssessmentAnswers, value: number) => void;
}) => (
  <div className="assessment-group">
    <h3 className="assessment-group-title">{title}</h3>
    {questions.map((q) => (
      <ScaleQuestionRow
        key={q.id}
        question={q}
        value={answers[q.id] as number | undefined}
        onSelect={(v) => onSet(q.id, v)}
      />
    ))}
  </div>
);

const ScaleQuestionRow = ({
  question,
  value,
  onSelect,
}: {
  question: { number: string; text: string; loLabel: string; hiLabel: string };
  value: number | undefined;
  onSelect: (value: number) => void;
}) => (
  <div className="assessment-question">
    <p className="assessment-question-text">
      <span className="assessment-question-number">{question.number}</span> {question.text}
    </p>
    <div className="assessment-scale">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`assessment-scale-btn${value === n ? ' selected' : ''}`}
          onClick={() => onSelect(n)}
          aria-label={`${question.number}: ${n}`}
          aria-pressed={value === n}
        >
          {n}
        </button>
      ))}
    </div>
    <div className="assessment-scale-labels">
      <span>{question.loLabel}</span>
      <span>{question.hiLabel}</span>
    </div>
  </div>
);

function ChoiceQuestion<T extends string>({
  number,
  text,
  options,
  value,
  onSelect,
}: {
  number: string;
  text: string;
  options: { value: T; label: string }[];
  value: T | undefined;
  onSelect: (value: T) => void;
}) {
  return (
    <div className="assessment-question">
      <p className="assessment-question-text">
        <span className="assessment-question-number">{number}</span> {text}
      </p>
      <div className="assessment-choices">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`assessment-choice-btn${value === opt.value ? ' selected' : ''}`}
            onClick={() => onSelect(opt.value)}
            aria-pressed={value === opt.value}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}
